import React, { useState, useEffect } from 'react';
import { userRequest } from '../../requestMethods';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    IconButton,
    Box,
    Typography,
    InputAdornment
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';

const DictionaryPhraseEditor = ({ open, onClose, phrase }) => {
    const [languages, setLanguages] = useState([]);
    const [formData, setFormData] = useState({
        phrase: '',
        displayPhrase: '',
        language: '',
        translations: []
    });
    const [searchQuery, setSearchQuery] = useState('');
    const [filteredPhrases, setFilteredPhrases] = useState([]);

    useEffect(() => {
        fetchLanguages();
    }, []);

    useEffect(() => {
        if (phrase) {
            // Convert populated language objects to their IDs for the form
            const formattedTranslations = phrase.translations ? phrase.translations.map(trans => ({
                language: trans.language?._id || trans.language || '',
                value: trans.value || ''
            })) : [];

            setFormData({
                phrase: phrase.phrase,
                displayPhrase: phrase.displayPhrase || '',
                language: phrase.language?._id || '',
                translations: formattedTranslations
            });
        } else {
            // Find English and O'zbekcha language IDs for defaults
            const englishLang = languages.find(lang => lang.name === 'English');
            const uzbekLang = languages.find(lang => lang.name === "O'zbekcha");
            
            setFormData({
                phrase: '',
                displayPhrase: '',
                language: englishLang?._id || '',
                translations: uzbekLang ? [{ language: uzbekLang._id, value: '' }] : []
            });
        }
    }, [phrase, languages]);

    const fetchLanguages = async () => {
        try {
            const response = await userRequest.get('/language');
            setLanguages(response.data);
        } catch (error) {
            console.error('Error fetching languages:', error);
        }
    };

    const handleSearch = async () => {
        if (!searchQuery.trim()) {
            setFilteredPhrases([]);
            return;
        }

        try {
            const response = await userRequest.get(`/dictionaryPhrases/search/${searchQuery}`);
            setFilteredPhrases(response.data);
        } catch (error) {
            console.error('Error searching phrases:', error);
            setFilteredPhrases([]);
        }
    };

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            handleSearch();
        }, 300);

        return () => clearTimeout(delayDebounceFn);
    }, [searchQuery]);

    const handleSubmit = async () => {
        try {
            if (phrase) {
                await userRequest.patch(`/dictionaryPhrases/${phrase._id}`, formData);
            } else {
                await userRequest.post('/dictionaryPhrases', formData);
            }
            onClose();
        } catch (error) {
            console.error('Error saving phrase:', error);
        }
    };

    const handleAddTranslation = () => {
        // Default to O'zbekcha for new translations
        const uzbekLang = languages.find(lang => lang.name === "O'zbekcha");
        setFormData(prev => ({
            ...prev,
            translations: [...prev.translations, { language: uzbekLang?._id || '', value: '' }]
        }));
    };

    const handleRemoveTranslation = (index) => {
        setFormData(prev => ({
            ...prev,
            translations: prev.translations.filter((_, i) => i !== index)
        }));
    };

    const handleTranslationChange = (index, field, value) => {
        setFormData(prev => ({
            ...prev,
            translations: prev.translations.map((trans, i) => 
                i === index ? { ...trans, [field]: value } : trans
            )
        }));
    };

    const handleSearchResultClick = (selectedPhrase) => {
        setFormData({
            phrase: selectedPhrase.phrase,
            displayPhrase: selectedPhrase.displayPhrase || '',
            language: selectedPhrase.language?._id || '',
            translations: selectedPhrase.translations || []
        });
        setSearchQuery('');
        setFilteredPhrases([]);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                {phrase ? 'Edit Phrase' : 'Add New Phrase'}
            </DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                    <TextField
                        label="Search Phrases"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        fullWidth
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                    />

                    {filteredPhrases.length > 0 && (
                        <Box sx={{ 
                            maxHeight: 200, 
                            overflowY: 'auto', 
                            border: '1px solid #ddd',
                            borderRadius: 1,
                            mb: 2
                        }}>
                            {filteredPhrases.map((p) => (
                                <Box 
                                    key={p._id}
                                    sx={{
                                        p: 1,
                                        cursor: 'pointer',
                                        '&:hover': {
                                            bgcolor: 'action.hover'
                                        },
                                        borderBottom: '1px solid #eee'
                                    }}
                                    onClick={() => handleSearchResultClick(p)}
                                >
                                    <Typography variant="subtitle1">
                                        {p.phrase}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {p.translations.map(t => `${t.language?.name}: ${t.value}`).join(', ')}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    )}

                    <TextField
                        label="Phrase"
                        value={formData.phrase}
                        onChange={(e) => setFormData(prev => ({ ...prev, phrase: e.target.value }))}
                        fullWidth
                        required
                    />

                    <TextField
                        label="Display Phrase (optional)"
                        value={formData.displayPhrase}
                        onChange={(e) => setFormData(prev => ({ ...prev, displayPhrase: e.target.value }))}
                        fullWidth
                        helperText="How this phrase should be displayed to users. e.g., 'start (something) off'"
                    />

                    <FormControl fullWidth>
                        <InputLabel>Language</InputLabel>
                        <Select
                            value={formData.language}
                            onChange={(e) => setFormData(prev => ({ ...prev, language: e.target.value }))}
                            label="Language"
                        >
                            {languages.map((lang) => (
                                <MenuItem key={lang._id} value={lang._id}>
                                    {lang.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Box sx={{ mt: 2 }}>
                        <Typography variant="h6" gutterBottom>
                            Translations
                            <IconButton 
                                color="primary" 
                                onClick={handleAddTranslation}
                                size="small"
                                sx={{ ml: 1 }}
                            >
                                <AddIcon />
                            </IconButton>
                        </Typography>

                        {formData.translations.map((trans, index) => (
                            <Box key={index} sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
                                <FormControl sx={{ flex: 1 }}>
                                    <InputLabel>Language</InputLabel>
                                    <Select
                                        value={trans.language}
                                        onChange={(e) => handleTranslationChange(index, 'language', e.target.value)}
                                        label="Language"
                                    >
                                        {languages.map((lang) => (
                                            <MenuItem key={lang._id} value={lang._id}>
                                                {lang.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                <TextField
                                    sx={{ flex: 2 }}
                                    label="Translation"
                                    value={trans.value}
                                    onChange={(e) => handleTranslationChange(index, 'value', e.target.value)}
                                />

                                <IconButton 
                                    color="error" 
                                    onClick={() => handleRemoveTranslation(index)}
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </Box>
                        ))}
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={handleSubmit} variant="contained" color="primary">
                    {phrase ? 'Update' : 'Create'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DictionaryPhraseEditor; 